export default {
  computed: {
    showVisumeDurationWarning() {
      const details = this.user?.personal_details;
      return details && details.video_resume_duration < 10 && !details.video_resume_verified;
    },
    showVisumeStatusCard() {
      if (!this.user?.personal_details) return false;
      return this.visumeMissing || this.showVisumeDurationWarning;
    },
    visumeMissing() {
      return !this.visume;
    },
    visumeBannerCtaLabel() {
      return this.visumeMissing ? 'Record visume' : 'Extend visume';
    },
    visumeStatusCardTitle() {
      if (this.visumeMissing) {
        return 'No visume yet — record a short introduction for recruiters.';
      }
      return `Your visume is only ${this.visumeDurationLabel} long`;
    },
    visumeStatusCardHint() {
      if (this.visumeMissing) {
        return '~2 min · Introduce yourself to recruiters';
      }
      return 'Aim for ~2 minutes about your skills and experience.';
    },
    visumeDurationSeconds() {
      return this.user?.personal_details?.video_resume_duration ?? 0;
    },
    visumeDurationLabel() {
      const seconds = this.visumeDurationSeconds;
      return seconds === 1 ? '1 second' : `${seconds} seconds`;
    },
    visumeDurationTarget() {
      return 120;
    },
    visumeDurationPercent() {
      return Math.min(100, Math.round((this.visumeDurationSeconds / this.visumeDurationTarget) * 100));
    },
    profileLocationLabel() {
      const city = this.user_city?.city_name;
      const sub = this.user?.personal_details?.sub_location?.sublocation_name;
      const parts = [];
      if (city) parts.push(city);
      if (sub && sub !== city) parts.push(sub);
      if (this.user_state && !parts.includes(this.user_state)) parts.push(this.user_state);
      return parts.length ? parts.join(' · ') : null;
    },
    educationLabel() {
      const qualification = this.user?.high_qualification;
      if (!qualification?.course?.course_name) return null;
      const branch = qualification.branch?.branch_name;
      return branch
        ? `${qualification.course.course_name} · ${branch}`
        : qualification.course.course_name;
    },
    visumeMetaLabel() {
      if (!this.visume) return 'Add visume';
      if (this.showVisumeDurationWarning) return `Visume · ${this.visumeDurationLabel}`;
      const duration = this.visumeDurationSeconds;
      return duration > 0 ? `Visume · ${this.visumeDurationLabel}` : 'Visume added';
    },
    resumeMetaLabel() {
      return this.resume_path ? 'Resume uploaded' : 'Add resume';
    },
    profileCompletionPercent() {
      return this.getProfileCompletionScore().percent;
    },
    profileCompletionMetaClass() {
      const percent = this.profileCompletionPercent;
      if (percent >= 80) return 'mph-chip--ok';
      if (percent >= 50) return 'mph-chip--mid';
      return 'mph-chip--warn';
    },
    profileCompletionHint() {
      const missing = this.getProfileCompletionScore().missing;
      if (!missing.length) return 'Your profile is well completed';
      return `Complete: ${missing.join(', ')}`;
    },
    profileCompletionChecklist() {
      return this.buildProfileCompletionChecklist();
    },
  },
  methods: {
    buildProfileCompletionChecklist() {
      if (!this.user || !Object.keys(this.user).length) return [];

      const user = this.user;
      const details = user?.personal_details;
      const career = user?.careerPreferencesDetail;
      const duration = details?.video_resume_duration ?? 0;
      const visumeQualityDone = details?.video_resume_verified || duration >= 10;
      const hasSkills = this.hasProfileSkills(details);
      const hasLanguages = this.hasProfileLanguages(user);
      const personalComplete = !!(
        details?.resume_summary &&
        details?.user_city &&
        details?.sub_location &&
        details?.gender &&
        details?.dob &&
        details?.marital_status &&
        hasSkills &&
        hasLanguages
      );

      return [
        {
          id: 'visume',
          label: this.visume ? 'Visume saved on your profile' : 'Add your visume video',
          shortLabel: 'visume',
          done: !!this.visume,
          maxPoints: 20,
          earnedPoints: this.visume ? 20 : 0,
          action: 'visume',
          actionLabel: 'Add',
          icon: 'mdi-video',
        },
        {
          id: 'visume-quality',
          label: visumeQualityDone ? 'Visume meets recommended length' : `Extend visume (${duration}s of 120s)`,
          shortLabel: 'visume length',
          done: this.visume ? visumeQualityDone : false,
          maxPoints: 10,
          earnedPoints: this.visume
            ? (visumeQualityDone ? 10 : Math.round((duration / 10) * 10))
            : 0,
          action: 'visume',
          actionLabel: 'Update',
          icon: 'mdi-clock-outline',
        },
        {
          id: 'resume',
          label: 'Upload your resume',
          shortLabel: 'resume',
          done: !!this.resume_path,
          maxPoints: 15,
          earnedPoints: this.resume_path ? 15 : 0,
          action: 'resume',
          actionLabel: 'Upload',
          icon: 'mdi-file-document-outline',
        },
        {
          id: 'email',
          label: 'Verify your email address',
          shortLabel: 'email verification',
          done: !!user?.email_verified,
          maxPoints: 5,
          earnedPoints: user?.email_verified ? 5 : 0,
          action: 'selectSettings',
          actionLabel: 'Update',
          icon: 'mdi-email-check-outline',
        },
        {
          id: 'personal',
          label: 'Complete personal details',
          shortLabel: 'personal details',
          done: personalComplete,
          maxPoints: 15,
          earnedPoints: personalComplete ? 15 : 0,
          action: 'personalDetails',
          actionLabel: 'Edit',
          icon: 'mdi-account-edit-outline',
        },
        {
          id: 'career',
          label: 'Set your preferred job role',
          shortLabel: 'job role',
          done: !!career?.fst_prefered_role,
          maxPoints: 10,
          earnedPoints: career?.fst_prefered_role ? 10 : 0,
          action: 'personalDetails',
          actionLabel: 'Set',
          icon: 'mdi-briefcase-outline',
        },
        {
          id: 'education',
          label: 'Add your education',
          shortLabel: 'education',
          done: !!user?.high_qualification?.course?.course_name,
          maxPoints: 10,
          earnedPoints: user?.high_qualification?.course?.course_name ? 10 : 0,
          action: 'educationalDetails',
          actionLabel: 'Add',
          icon: 'mdi-school-outline',
        },
        {
          id: 'experience',
          label: 'Add work experience',
          shortLabel: 'work experience',
          done: this.hasWorkExperienceComplete(user, details),
          maxPoints: 5,
          earnedPoints: this.hasWorkExperienceComplete(user, details) ? 5 : 0,
          action: 'experienceDetails',
          actionLabel: 'Add',
          icon: 'mdi-domain',
        },
      ];
    },
    getProfileCompletionScore() {
      const checklist = this.buildProfileCompletionChecklist();
      const score = checklist.reduce((sum, item) => sum + item.earnedPoints, 0);
      const missing = checklist.filter((item) => !item.done).map((item) => item.shortLabel);

      return {
        score,
        percent: Math.min(100, score),
        missing,
        checklist,
      };
    },
    hasProfileSkills(details) {
      if (!details?.skills) return false;
      try {
        const parsed = JSON.parse(details.skills);
        return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
      } catch {
        return !!details.skills;
      }
    },
    hasProfileLanguages(user) {
      const raw = user?.languages_known?.language_known;
      if (!raw) return false;
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
      } catch {
        return !!raw;
      }
    },
    hasWorkExperienceComplete(user, details) {
      if (details?.total_work_experience != null && details?.total_work_experience !== '') {
        return true;
      }
      return !!(user?.current_company || user?.previous_companies?.length);
    },
  },
};
